import * as Yup from "yup";
import { toast } from "react-toastify";
import { GrView } from "react-icons/gr";
import { useEffect, useState } from "react";

import { SearchIcon } from "../../assets/Icons";
import { ModalContainer } from "../../components/PageComponents";
import ProfileImage from "../../../src/assets/images/ProfileImage.svg";
import { LabelInput } from "../../components/Reusable/Layout";
import { FloatingLabelSelect } from "../../components/Reusable/Layout";
import { Button } from "../../components/Reusable/Button";
import { CloseIcon } from "../../icons/promotions";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { createAdmin, updateUser } from "../../features/auth/store/userSlice";
import { RootState } from "../../redux/store";
import { CreateAdminData } from "../../features/auth/store/types/user-types";
import {
  UserTableProps,
  AdminSearchFilterProps,
  UserDetailModalProps,
} from "./types";

export const ADMIN_ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Administrator" },
  { value: "admin", label: "Administrator" },
  { value: "finance_admin", label: "Finance Admin" },
  { value: "accountant", label: "Accountant" },
  { value: "support_admin", label: "Customer Service Admin" },
  { value: "customer_service", label: "Customer Service" },
  { value: "developer", label: "Developer " },
];

export const ADMIN_ROLE_VALUES = ADMIN_ROLE_OPTIONS.map((role) => role.value);

export const UsersTable = ({
  users,
  onUserClick,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  total,
  perPage,
}: UserTableProps) => {
  const goToNextPage = () => {
    if (onPageChange && currentPage && totalPages) {
      if (currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    }
  };

  const goToPreviousPage = () => {
    if (onPageChange && currentPage) {
      if (currentPage > 1) {
        onPageChange(currentPage - 1);
      }
    }
  };

  return (
    <div className="flex flex-col w-full">
      <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-2xl border border-[#FFFFFF1A]">
        <table className="w-full min-w-[800px] overflow-hidden">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs sm:text-sm leading-normal h-[50px]">
              <th className="px-4 sm:px-6 text-left">
                <div className="flex items-center gap-1">
                  <input type="checkbox" className="mr-2" />
                  <p>Email Address</p>
                </div>
              </th>
              <th className="px-4 sm:px-6 text-left">Phone Number</th>
              <th className="px-4 sm:px-6 text-left">First Name</th>
              <th className="px-4 sm:px-6 text-left">Last Name</th>
              <th className="px-4 sm:px-6 text-left">Role</th>
              <th className="px-4 sm:px-6 text-left">Country</th>
              <th className="px-4 sm:px-6 text-left">View</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs sm:text-sm">
            {loading ? (
              <tr className="border-b border-gray-200 h-[50px]">
                <td colSpan={7} className="px-6 text-center">
                  Loading...
                </td>
              </tr>
            ) : users.length > 0 ? (
              users.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-gray-200 h-[50px] hover:bg-gray-50 dark:hover:bg-[#ffffff05]"
                >
                  <td className="px-4 sm:px-6 py-4 align-middle">
                    <div className="flex items-center gap-3">
                      <span>{user.avatar}</span>
                      <span className="truncate max-w-[180px] sm:max-w-none">
                        {user.email}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6">{user.phone_number}</td>
                  <td className="px-4 sm:px-6">{user.firstname}</td>
                  <td className="px-4 sm:px-6">{user.lastname}</td>
                  <td className="px-4 sm:px-6">{user.role}</td>
                  <td className="px-4 sm:px-6">{user.country}</td>
                  <td
                    onClick={() => onUserClick(user)}
                    className="px-4 sm:px-6 cursor-pointer text-blue-500 hover:text-blue-600"
                  >
                    <GrView />
                  </td>
                </tr>
              ))
            ) : (
              <tr className="border-b border-gray-200 h-[50px]">
                <td colSpan={7} className="px-6 text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center py-4 border-t border-[#FFFFFF1A] mt-4 gap-2">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={goToPreviousPage}
          disabled={currentPage === 1 || loading}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs sm:text-sm whitespace-nowrap">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === totalPages || totalPages === 0 || loading
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          onClick={goToNextPage}
          disabled={currentPage === totalPages || totalPages === 0 || loading}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const AdminSearchFilter: React.FC<AdminSearchFilterProps> = ({
  onFilterChange,
  filters,
  filterOptions,
  countries,
  loading,
  onClick,
}) => {
  return (
    <div className="flex flex-col gap-3 mb-6 w-full lg:flex-row lg:items-center lg:justify-between">
      <div className="relative w-full lg:max-w-[400px] xl:max-w-[500px]">
        <input
          type="text"
          onChange={(e) => onFilterChange("search", e.target.value)}
          placeholder="Search for a user..."
          className="border p-2 pl-10 rounded-3xl w-full h-[44px] sm:h-[48px] dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A]"
        />
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
          <SearchIcon />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center w-full lg:w-auto">
        <select
          name="country"
          onChange={(e) => onFilterChange("country", e.target.value)}
          className="border p-2 h-[44px] rounded-3xl bg-[#F4F4F5] text-black border-gray-300 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46] w-full sm:w-[130px]"
        >
          <option value="" className="dark:text-white">
            Country
          </option>
          {countries.map((country) => (
            <option
              key={country.id}
              value={country.name}
              className="dark:bg-[#27272A] dark:text-white"
            >
              {country.name}
            </option>
          ))}
        </select>

        <select
          name="role"
          onChange={(e) => onFilterChange("role", e.target.value)}
          className="border p-2 bg-[#F4F4F5] h-[44px] rounded-3xl dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46] w-full sm:w-[130px]"
        >
          <option value="">Role</option>
          {ADMIN_ROLE_OPTIONS.map((role) => (
            <option key={role.value} value={role.value}>
              {role.label}
            </option>
          ))}
        </select>

        <select
          name="status"
          onChange={(e) => onFilterChange("status", e.target.value)}
          className="border p-2 bg-[#F4F4F5] h-[44px] rounded-3xl dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46] w-full sm:w-[130px] col-span-2 sm:col-span-1"
        >
          {filterOptions?.status_options?.map((status: string) => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>

        <Button
          onClick={onClick}
          bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
          textColor="text-white"
          borderRadius="rounded-xl"
          className="w-full sm:w-auto h-[44px] px-6 col-span-2 sm:col-span-1 sm:ml-2"
        >
          Create admin
        </Button>
      </div>
    </div>
  );
};

export const UserDetailModal = ({
  user,
  showTabs = true,
  tabItems = ["Personal Information", "Role"],
  onClose,
}: UserDetailModalProps) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state: RootState) => state.users);
  const [toggle, setToggle] = useState<string>(tabItems[0]);
  const [selectedRole, setSelectedRole] = useState<string>(user?.role || "");

  useEffect(() => {
    setSelectedRole(user?.role || "");
  }, [user]);

  const handleSaveRole = async () => {
    const userId = user?.user_id || user?.id;
    if (!userId) {
      toast.error("Unable to update role for this user");
      return;
    }

    try {
      await dispatch(
        updateUser({
          user_id: Number(userId),
          data: { role: selectedRole },
        }),
      ).unwrap();
      toast.success("Role updated successfully!");
      onClose();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const renderContent = () => {
    switch (toggle) {
      case "Personal Information":
        return (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <LabelInput
                  type="text"
                  value={user?.country || ""}
                  placeholder="Country"
                  name="country"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <LabelInput
                  type="text"
                  value={user?.firstname || ""}
                  placeholder="First Name"
                  name="firstName"
                />
              </div>
              <div>
                <LabelInput
                  type="text"
                  value={user?.lastname || ""}
                  placeholder="Last Name"
                  name="lastName"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <LabelInput
                  type="email"
                  value={user?.email || ""}
                  placeholder="Email Address"
                  name="email"
                />
              </div>
              <div>
                <LabelInput
                  type="tel"
                  value={user?.phone_number || ""}
                  placeholder="Phone Number"
                  name="phone"
                />
              </div>
            </div>
            <div>
              <LabelInput
                type="text"
                value={user?.birthdate || ""}
                placeholder="Birth Date"
                name="birthDate"
              />
            </div>
            <div className="flex justify-end pt-1">
              <button className="w-full sm:w-auto border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-xl text-xs sm:text-sm dark:text-white dark:bg-[#FFFFFF1A]">
                Save changes
              </button>
            </div>
          </div>
        );
      case "Role":
        return (
          <div className="mt-4 space-y-3">
            <FloatingLabelSelect
              value={selectedRole}
              placeholder="Select Role"
              name="role"
              onChange={(e) => setSelectedRole(e.target.value)}
              options={ADMIN_ROLE_OPTIONS}
            />
            <div className="flex justify-end pt-1">
              <button
                onClick={handleSaveRole}
                disabled={loading || !selectedRole}
                className="w-full sm:w-auto border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-xl text-xs sm:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save role"}
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <ModalContainer
      title="User Details"
      closeIcon={
        <button
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A]"
        >
          <CloseIcon />
        </button>
      }
    >
      {/* This wrapper caps the height on mobile viewports and enables clean scrolling 
        so the container never bleeds out of the device viewport screens.
      */}
      <div className="max-h-[calc(100vh-140px)] overflow-y-auto px-1 pr-1.5 scrollbar-thin">
        {/* Profile Card Header: Scaled down spacing and layout gaps for mobile */}
        <div className="bg-BG-light dark:bg-BG-dark flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left p-3 sm:p-4 rounded-xl mb-3 gap-3">
          <div className="flex-shrink-0">
            <img
              src={user?.profile_image || ProfileImage}
              alt="Profile"
              className="w-[72px] h-[72px] sm:w-[100px] sm:h-[100px] rounded-full object-cover"
            />
          </div>

          <div className="flex flex-col justify-center h-full space-y-1 w-full overflow-hidden">
            <p className="font-bold text-lg sm:text-xl lg:text-2xl dark:text-white truncate">
              {user
                ? `${user.firstname || ""} ${user.lastname || ""}`.trim() ||
                  "N/A"
                : "Loading..."}
            </p>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-2 gap-y-0.5 dark:text-white text-xs sm:text-sm opacity-80">
              <p className="truncate max-w-[160px] sm:max-w-none">
                {user?.username || "N/A"}
              </p>
              <span className="hidden sm:inline">|</span>
              <p>
                Joining date:{" "}
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-lg w-fit mx-auto sm:mx-0 mt-1 px-2.5 py-0.5 text-xs font-medium">
              {user?.status || "Unknown"}
            </div>
          </div>
        </div>

        {/* Tab Selection Row */}
        {showTabs && tabItems.length > 0 && (
          <div className="rounded-xl p-[4px] bg-[#F4F4F5] dark:bg-BG-dark w-full sm:w-[320px] h-[38px] flex mb-3">
            {tabItems.map((item, index) => (
              <button
                key={index}
                className={`flex-1 py-[4px] px-[8px] text-xs font-medium transition-all duration-200 ${
                  toggle === item
                    ? "rounded-lg dark:text-white bg-white dark:bg-[#FFFFFF1A] shadow-sm font-semibold"
                    : "dark:text-main-dark opacity-70"
                }`}
                onClick={() => setToggle(item)}
              >
                {item}
              </button>
            ))}
          </div>
        )}

        {/* Render Form Content */}
        {renderContent()}
      </div>
    </ModalContainer>
  );
};

export const CreateAdminModal = ({
  onClose,
  isOpen,
  handleOverlayClick,
  handleModalClick,
}: {
  onClose: () => void;
  isOpen: boolean;
  handleOverlayClick: (e: React.MouseEvent) => void;
  handleModalClick: (e: React.MouseEvent) => void;
}) => {
  const adminSchema = Yup.object().shape({
    username: Yup.string().required("Username is required"),
    firstname: Yup.string().required("First name is required"),
    lastname: Yup.string().required("Last name is required"),
    othername: Yup.string().required("Other name is required"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    phone_number: Yup.string()
      .min(10, "Phone number must be at least 10 characters")
      .max(15, "Phone number must not exceed 15 characters")
      .required("Phone number is required"),
    language: Yup.string().required("Language is required"),
    country: Yup.string().required("Country is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters long")
      .required("Password is required"),
    role: Yup.string().required("Role is required"),
  });

  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state: RootState) => state.users);

  const [formData, setFormData] = useState<CreateAdminData>({
    username: "",
    firstname: "",
    lastname: "",
    othername: "",
    role: "admin",
    phone_number: "",
    email: "",
    language: "",
    country: "",
    password: "",
  });

  const [errors, setErrors] = useState<Partial<CreateAdminData>>({});

  const validateForm = async (): Promise<boolean> => {
    try {
      await adminSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationError: any) {
      const newErrors: Partial<CreateAdminData> = {};
      if (validationError.inner) {
        validationError.inner.forEach((err: any) => {
          if (err.path)
            newErrors[err.path as keyof CreateAdminData] = err.message;
        });
      }
      setErrors(newErrors);
      return false;
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof CreateAdminData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) return;

    try {
      const result = await dispatch(createAdmin(formData));
      if (createAdmin.fulfilled.match(result)) {
        setFormData({
          username: "",
          firstname: "",
          lastname: "",
          othername: "",
          role: "admin",
          phone_number: "",
          email: "",
          language: "",
          country: "",
          password: "",
        });
        setErrors({});
        onClose();
        toast("Admin created successfully!");
      }
    } catch (error) {
      console.error("Failed to create admin:", error);
    }
  };

  if (!isOpen) return null;

  return (
    /* 1. We intercept the overlay backdrop context layout right here.
      2. 'p-4' ensures a strict 16px safety perimeter so it never hits the glass edge.
      3. 'items-center' perfectly drops it into the true vertical midpoint.
    */
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-lg" onClick={handleModalClick}>
        <ModalContainer
          title="Create Admin"
          closeIcon={
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A]"
            >
              <CloseIcon />
            </button>
          }
        >
          {/* Confined max-height prevents container expansion past the viewable screen layout */}
          <form
            onSubmit={handleSubmit}
            className="mt-3 max-h-[calc(100vh-200px)] overflow-y-auto px-1 pr-1.5 space-y-3.5 scrollbar-thin"
          >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Fields Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              <div>
                <LabelInput
                  type="text"
                  value={formData.username}
                  placeholder="Username"
                  name="username"
                  onChange={handleInputChange}
                />
                {errors.username && (
                  <p className="text-red-500 text-xs mt-1">{errors.username}</p>
                )}
              </div>
              <div>
                <LabelInput
                  type="text"
                  value={formData.firstname}
                  placeholder="First Name"
                  name="firstname"
                  onChange={handleInputChange}
                />
                {errors.firstname && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.firstname}
                  </p>
                )}
              </div>
              <div>
                <LabelInput
                  type="text"
                  value={formData.lastname}
                  placeholder="Last Name"
                  name="lastname"
                  onChange={handleInputChange}
                />
                {errors.lastname && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastname}</p>
                )}
              </div>
            </div>

            <div>
              <LabelInput
                type="text"
                value={formData.othername}
                placeholder="Other Name"
                name="othername"
                onChange={handleInputChange}
              />
              {errors.othername && (
                <p className="text-red-500 text-xs mt-1">{errors.othername}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <LabelInput
                  type="email"
                  value={formData.email}
                  placeholder="Email Address"
                  name="email"
                  onChange={handleInputChange}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>
              <div>
                <LabelInput
                  type="tel"
                  value={formData.phone_number}
                  placeholder="Phone Number"
                  name="phone_number"
                  onChange={handleInputChange}
                />
                {errors.phone_number && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.phone_number}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <LabelInput
                  type="text"
                  value={formData.language}
                  placeholder="Language"
                  name="language"
                  onChange={handleInputChange}
                />
                {errors.language && (
                  <p className="text-red-500 text-xs mt-1">{errors.language}</p>
                )}
              </div>
              <div>
                <LabelInput
                  type="text"
                  value={formData.country}
                  placeholder="Country"
                  name="country"
                  onChange={handleInputChange}
                />
                {errors.country && (
                  <p className="text-red-500 text-xs mt-1">{errors.country}</p>
                )}
              </div>
            </div>

            <div>
              <LabelInput
                type="password"
                value={formData.password}
                placeholder="Password"
                name="password"
                onChange={handleInputChange}
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>

            <div>
              <FloatingLabelSelect
                value={formData.role}
                placeholder="Select Role"
                name="role"
                onChange={handleInputChange}
                options={ADMIN_ROLE_OPTIONS}
              />
              {errors.role && (
                <p className="text-red-500 text-xs mt-1">{errors.role}</p>
              )}
            </div>

            <div className="flex justify-end pt-1">
              <Button
                type="submit"
                disabled={loading}
                bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
                textColor="text-white"
                borderRadius="rounded-xl"
                className="w-full sm:w-[156px] h-11 text-sm font-medium"
              >
                {loading ? "Creating..." : "Create Account"}
              </Button>
            </div>
          </form>
        </ModalContainer>
      </div>
    </div>
  );
};
